import React, { useState } from 'react';

import { formatServicePeriod } from '~support-at-home/lib/servicePeriod';

import { Button } from '~components';
import Icon, { faCalendar } from '~components/icon';
import PopOver from '~components/pop-over/PopOver';
import { Row } from '~components/table-view';
import { dateEditor } from '~components/table-view/columnEditors';

export interface ContributionPeriod {
  start_date: string | null;
  end_date: string | null;
}

interface ComponentProps {
  row: Row;
  onChange: (period: ContributionPeriod) => void;
  disabled?: boolean;
}

const startEditor = dateEditor('start_date');
const endEditor = dateEditor('end_date');

// Edit mode for the Period column. Two full date pickers do not fit the column without widening the
// table (which would then scroll sideways), so the cell shows the period as one field-like control and
// the pickers open side by side in a popover below it. Each change reports both dates, so the
// column's onChange can write them back to the row as the two fields the API expects.
const PeriodEditorControl: React.FC<ComponentProps> = ({ row, onChange, disabled = false }) => {
  const [isOpen, setOpen] = useState(false);
  const start_date = row.start_date ?? null;
  const end_date = row.end_date ?? null;
  const targetId = `sah-client-contributions-period-${row.id}`;
  const label = formatServicePeriod(start_date, end_date) || 'Choose dates';

  return (
    <>
      <button
        id={targetId}
        type="button"
        className="form-control form-control-sm contribution-period-trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Edit period: ${label}`}
        title={label}
        onClick={() => setOpen((open) => !open)}
      >
        <span className="contribution-truncated-text">{label}</span>
        <Icon icon={faCalendar} />
      </button>
      <PopOver
        isOpen={isOpen}
        target={targetId}
        placement="bottom"
        autoFlip
        toggle={() => setOpen(false)}
        ariaLabel="Contribution period"
      >
        <div className="contribution-period-editor">
          <label>
            Start
            {startEditor(
              start_date,
              row,
              (value: string | null) => onChange({ start_date: value, end_date }),
              {},
              disabled,
            )}
          </label>
          <label>
            End (optional)
            {endEditor(
              end_date,
              row,
              (value: string | null) => onChange({ start_date, end_date: value }),
              {},
              disabled,
            )}
          </label>
          <Button size="sm" color="success" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopOver>
    </>
  );
};

// Called as a plain function from list.ts (a .ts file, so no JSX there); returning an element rather
// than rendering inline lets the control keep its own open state.
const PeriodEditor = (props: ComponentProps) => <PeriodEditorControl {...props} />;

export default PeriodEditor;
